/**
 * In-memory session. The access token lives here and nowhere else (spec §5.1: "frontend holds it in
 * memory only (not localStorage)"). The refresh token is an httpOnly cookie the browser owns.
 */
import { create } from 'zustand'

import type { Capabilities, SessionResponse, SessionUser } from '@/api/types'

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated'

export type LogoutReason =
  | 'user'
  | 'expired'
  | 'invalid'
  /** TOKEN_REPLAY_DETECTED — the whole token family was revoked. Never retry; tell the user. */
  | 'replay'
  | 'other-tab'

export interface SessionState {
  readonly status: SessionStatus
  readonly accessToken: string | null
  /** Epoch milliseconds at which the access token expires. */
  readonly expiresAt: number | null
  readonly user: SessionUser | null
  readonly capabilities: Capabilities | null
  /** True when `GET /capabilities` answered 403 — a scoped role with no locations (ADR-029). */
  readonly capabilitiesDenied: boolean
  readonly lastLogoutReason: LogoutReason | null
  setSession: (body: SessionResponse, now?: number) => void
  setCapabilities: (capabilities: Capabilities | null) => void
  setCapabilitiesDenied: (denied: boolean) => void
  clear: (reason: LogoutReason | null) => void
}

const EMPTY = {
  status: 'anonymous' as const,
  accessToken: null,
  expiresAt: null,
  user: null,
  capabilities: null,
  capabilitiesDenied: false,
}

export const useSessionStore = create<SessionState>()((set) => ({
  ...EMPTY,
  status: 'unknown',
  lastLogoutReason: null,
  setSession: (body, now = Date.now()) => {
    set({
      status: 'authenticated',
      accessToken: body.access_token,
      expiresAt: now + body.expires_in * 1000,
      user: body.user,
      lastLogoutReason: null,
    })
  },
  setCapabilities: (capabilities) => {
    set({ capabilities, capabilitiesDenied: false })
  },
  setCapabilitiesDenied: (denied) => {
    set((state) => ({
      capabilitiesDenied: denied,
      capabilities: denied ? null : state.capabilities,
    }))
  },
  clear: (reason) => {
    set({ ...EMPTY, lastLogoutReason: reason })
  },
}))

/** Non-React read, for the API client. */
export function getAccessToken(): string | null {
  return useSessionStore.getState().accessToken
}
